<template>
    <Modal id="ticket-picker-modal"
           data-component="TicketPickerModal"
           size="lg"
           icon="fa-ticket-simple"
           :custom-title-text="title"
           :loading="loading">
        <TicketPickerSession v-if="session"
                             :session="session"
                             :spaces-required="selectedPeople"></TicketPickerSession>

        <div class="item-panel">
            <!-- On mobile collapse the category list into a dropdown to save vertical space. -->
            <CategoryNavDropdown v-model="selectedCategory"
                                 :categories="visibleCategories"
                                 analytics-id-prefix="Ticket Picker"
                                 class="mobile-only ticket-picker__category-dropdown" />

            <div class="item-nav non-mobile-only">
                <ul>
                    <li v-for="(category, index) in visibleCategories"
                        :key="category.ID"
                        :class="{active: category.ID === selectedCategory.ID}"
                        class="ticket-picker__category">
                        <a :data-analytics-id="`Ticket Picker / Category Select / Index: ${index}`"
                           @click.prevent="selectedCategory = category">
                            <i v-if="category.ID === FAVOURITE_CAT_ID"
                               class="fa-solid fa-star" />
                            <span class="category-name">{{ category.name }}</span>
                            <i class="fa-regular fa-chevron-right"></i>
                        </a>
                    </li>
                </ul>
            </div>

            <div class="item-panel__right">
                <div v-if="!visibleTickets.length"
                     class="alert alert-warning">
                    {{ l('TICKET_BOOKING.NO_TICKET_TYPES_FOR_SESSION') }}
                </div>

                <div v-for="ticket in visibleTickets"
                     :key="ticket.ID"
                     class="ticket-picker__ticket">
                    <div class="ticket-picker__ticket-details">
                        {{ ticket.getName() }}

                        <i v-if="ticket.hasWaiver"
                           class="fa-solid fa-file-signature ticket-picker__waiver-icon" />

                        <i v-if="ticket.hasSeats"
                           class="fa-solid fa-chair ticket-picker__seats-icon" />

                        <div class="ticket-picker__ticket-price">
                            <template v-if="adjustedPrices[ticket.ID] !== undefined && adjustedPrices[ticket.ID] !== ticket.getPrice()">
                                <small class="strike">{{ currencyFilter(ticket.getPrice()) }}</small>
                                {{ currencyFilter(adjustedPrices[ticket.ID]) }}
                            </template>
                            <template v-else>
                                {{ currencyFilter(ticket.getPrice()) }}
                            </template>
                        </div>
                    </div>
                    <div class="ticket-picker__ticket-btns">
                        <button class="round-btn round-btn--lg round-btn-decrease"
                                :disabled="ticketTypeDisabled(ticket) || minusButtonDisabled(ticket.ID)"
                                @click="decreaseTicketQuantity(ticket.ID)">
                            <i class="fa-regular fa-minus"></i>
                        </button>
                        <input v-model="ticketQuantities[ticket.ID]"
                               :disabled="ticketTypeDisabled(ticket)"
                               type="number"
                               min="0"
                               :class="{ invalid: invalidTicketQuantity(ticket.ID) }"
                               class="ticket-picker__quantity-input" />
                        <button class="round-btn round-btn--lg round-btn-increase"
                                :disabled="ticketTypeDisabled(ticket)"
                                @click="increaseTicketQuantity(ticket.ID)">
                            <i class="fa-regular fa-plus"></i>
                        </button>
                    </div>
                </div>

                <div v-if="hasDisabledTicketsDueToOfflineWaivers"
                     class="alert alert-info alert--small text-center">
                    <i class="fa-solid fa-info-circle"></i>
                    {{ l('SELL.WAIVER_UNAVAILABLE_OFFLINE') }}
                </div>
            </div>
        </div>
        <template v-slot:footer>
            <div class="modl-footer">
                <button class="btn btn-lg btn-default cancel-btn modl__cancel-btn"
                        @click.prevent="cancel">
                    {{ l('CANCEL') }}
                </button>

                <Toggle v-if="deviceCanManageGroupBookings"
                        v-model="assignGroupBookingReference"
                        name="assign-group-reference"
                        label="GROUP_BOOKINGS.ASSIGN_GROUP_REFERENCE" />

                <button class="btn btn-lg ok-btn btn-primary"
                        :disabled="!okButtonEnabled"
                        @click.prevent="ok">
                    {{ l('CONTINUE') }}
                </button>
            </div>
        </template>
    </Modal>
</template>

<script>
import Category from '@/lib/Categories/Category';
import CategoryNav from '@/epos/vue/components/Generic/CategoryNav';
import CategoryNavDropdown from '@/epos/vue/components/Generic/CategoryNavDropdown';
import Modal from '@/vue/components/Modals/Modal';
import TicketPickerSession from '@/epos/vue/modals/TicketPicker/TicketPickerSession';
import Toggle from '@/vue/components/Forms/Toggle';
import { CloseModalOnEscapeMixin } from '@/vue/mixins/CloseModalOnEscapeMixin';
import { container } from '@/container';
import { mapState } from 'vuex';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { sortByNavOrder } from '@/functions/arraySorting';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';
import { toInt, toNullableFloat } from '@/functions/transform';

const { FAVOURITE_CAT_ID } = CategoryNav;

export default {
    components: {
        Toggle,
        Modal,
        CategoryNavDropdown,
        TicketPickerSession,
    },

    mixins: [
        CloseModalOnEscapeMixin,
        ModalPropsMixin,
    ],

    props: {
        /**
         * Tickets from all categories.
         *
         * @type {Ticket[]}
         */
        allTicketsForEvent: {
            type: Array,
            required: true,
        },

        /**
         * @type {Category[]}
         */
        allCategoriesForEvent: {
            type: Array,
            required: true,
        },

        /**
         * @type {SessionEvent}
         */
        event: {
            type: Object,
            required: true,
        },

        groupBookingReference: {
            type: String,
            default: null,
        },

        initialQuantityData: {
            type: Array,
            default: null,
        },

        initialSelectedCategory: {
            type: Object,
            default: null,
        },

        session: {
            type: Object,
            default: null,
        },

        okEnabled: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            FAVOURITE_CAT_ID,

            /**
             * Show a loading spinner in the modal footer?
             *
             * @type {Boolean}
             */
            loading: false,

            /**
             * @type {Category}
             */
            selectedCategory: this.initialSelectedCategory,

            /**
             * Simple key/value object of ticketIDs and the qty of that ticket selected.
             *
             * Keys are the ticketIDs.
             *
             * @type {Object.<number, number>}
             */
            ticketQuantities: {},

            /**
             * The category of the last ticket added in the modal. We store this so that if the operator comes back
             * to the modal either after cancelling at the session picker, or comes back to add more tickets, the
             * last category they used will be selected again.
             *
             * @type {Category}
             */
            lastTicketSelectedCategory: this.initialSelectedCategory,

            /**
             * Holds the adjusted price (based on pricing rules) for each ticket displayed.
             * This price does not get attached to the Ticket object, because the same Ticket object is shared
             * between the Cart and multiple instances of the TicketPickerModal.
             *
             * Keys are the ticketIDs.
             *
             * @type {Object.<number, number>}
             */
            adjustedPrices: {},

            /**
             * The state of the toggle that controls whether we show the GroupBookingReferenceModal
             * when we click the continue button.
             * This value is persisted to the cart (and to local storage to remember it for the next cart) when the
             * modal is submitted. It is not saved if the modal is cancelled.
             *
             * @type {boolean}
             */
            assignGroupBookingReference: container.cartService.currentCart.assignGroupBookingReference,
        };
    },

    computed: {
        ...mapState(['isOnline']),

        ...mapState('cart', ['currentCartIsEditingOrder']),

        /**
         * @return {string}
         */
        title() {
            return container.langService.getText(
                'TICKET_BOOKING.TICKET_PICKER_MODAL_TITLE',
                {
                    eventName: this.event.name,
                }
            );
        },

        /**
         * @return {number[]}
         */
        favouriteItemIDs() {
            return container.currentDevice.getDevice()?.deviceGroup?.favouriteItemIDs ?? [];
        },

        /**
         * @return {Ticket[]}
         */
        favouriteTickets() {
            if (this.favouriteItemIDs.length) {
                return this.allTicketsForEvent.filter((ticket) => this.favouriteItemIDs.includes(ticket.ID));
            }
            return [];
        },

        /**
         * @return {Category|null}
         */
        favouritesCat() {
            if (!this.favouriteTickets.length) {
                return null;
            }
            const cat = new Category({
                ID: FAVOURITE_CAT_ID,
                name: container.langService.getText('SELL.FAVOURITES'),
                navOrder: FAVOURITE_CAT_ID,
            });
            return cat;
        },

        /**
         * Only enable the OK button to submit the modal if at least one ticket is selected.
         *
         * If the device is offline and one of the selected tickets has waivers, disable the OK button.
         *
         * @return {boolean}
         */
        okButtonEnabled() {
            // We require:
            // - At least one ticket selected with a non-zero qty?
            // - No tickets with (invalid) or -ve values

            let totalQty = 0;
            for (let ticketID of Object.keys(this.ticketQuantities)) {
                ticketID = parseInt(ticketID);
                const qty = this.ticketQuantities[ticketID];
                if (qty < 0) {
                    return false;
                }
                totalQty += qty;

                if (qty > 0 && !this.isOnline) {
                    /**
                     * @type {Ticket}
                     */
                    const ticket = this.allTicketsForEvent.find((t) => t.ID === ticketID);
                    if (ticket?.hasWaiver) {
                        return false;
                    }
                }
            }

            return totalQty > 0;
        },

        /**
         * @return {Category[]}
         */
        visibleCategories() {
            const sortedCats = sortByNavOrder([...this.allCategoriesForEvent]);
            return this.favouritesCat
                ? [this.favouritesCat, ...sortedCats]
                : sortedCats;
        },

        /**
         * @return {Ticket[]}
         */
        visibleTickets() {
            if (!this.selectedCategory) {
                return [];
            }

            const tickets = this.selectedCategory.ID === FAVOURITE_CAT_ID
                ? this.favouriteTickets
                : this.allTicketsForEvent.filter(
                    (ticket) => ticket.category?.ID === this.selectedCategory.ID
                );
            const sortedTickets = sortByNavOrder([...tickets]);

            return sortedTickets;
        },

        ticketLines() {
            return this.allTicketsForEvent.map((ticket) => {
                const qty = toInt(this.ticketQuantities[ticket.ID]);
                if (qty !== 0) {
                    return {
                        ticket,
                        qty,
                    };
                }

                return null;
            }).filter((t) => !!t);
        },

        selectedPeople() {
            return this.ticketLines.reduce((total, {
                ticket,
                qty,
            }) => total + (qty * ticket.people), 0);
        },

        hasDisabledTicketsDueToOfflineWaivers() {
            if (this.isOnline) {
                return false;
            }

            for (let i = 0; i < this.visibleTickets.length; i++) {
                if (this.visibleTickets[i].hasWaiver) {
                    return true;
                }
            }

            return false;
        },

        deviceCanManageGroupBookings() {
            return this.$store.state.currentDevice.manageGroupBookings;
        },
    },

    watch: {
        visibleTickets: {
            handler() {
                // Fetch new adjusted prices when changing the category (which changes the visible tickets).
                this.updateTicketPrices();
            },
            immediate: true,
            // When using the watch option to watch an array, the callback will only trigger when the array is replaced.
            // In other words, the watch callback will no longer be triggered on array mutation.
            // To trigger on mutation, the deep option must be specified.
            deep: true,
        },
    },

    created() {
        if (this.initialQuantityData) {
            this.initialQuantityData.forEach(({
                ticket,
                qty,
            }) => {
                this.ticketQuantities[ticket.ID] = qty;
            });
        }

        // Set the qty of any tickets whose qty was not already specified to 0.
        // We need to do this so Vue adds its reactivity to the new properties.
        this.allTicketsForEvent.forEach((ticket) => {
            if (!this.ticketQuantities.hasOwnProperty(ticket.ID)) {
                this.ticketQuantities[ticket.ID] = 0;
            }
        });

        if (this.initialSelectedCategory) {
            // Check if the initialSelectedCategory exists.
            if (!this.visibleCategories.find((cat) => cat.ID === this.initialSelectedCategory.ID)) {
                this.selectedCategory = null;
            }
        }

        // Select the first category by default. selectedCategory will be null upon creation if no
        // initialSelectedCategory was supplied. This needs to happen here in created() instead of data() because
        // computed values are not available yet in data, and visibleCategories is computed.
        if (!this.selectedCategory) {
            this.selectedCategory = this.visibleCategories[0];
        }

        if (this.groupBookingReference) {
            // If a group booking reference was given in the props we'll use that one.
            // Ensure the toggle is on so that this works.
            this.assignGroupBookingReference = true;
        }
    },

    methods: {
        increaseTicketQuantity(ticketID) {
            this.ticketQuantities[ticketID] = toInt(this.ticketQuantities[ticketID]) + 1;
            this.lastTicketSelectedCategory = this.selectedCategory;
        },

        decreaseTicketQuantity(ticketID) {
            this.ticketQuantities[ticketID] = toInt(this.ticketQuantities[ticketID]) - 1;
        },

        /**
         * @param {Ticket} ticket
         */
        ticketTypeDisabled(ticket) {
            if (!this.isOnline && ticket.hasWaiver) {
                // Can't sell tickets with waivers while offline.
                return true;
            }

            if (this.currentCartIsEditingOrder && ticket.hasBookableResources) {
                // Can't add tickets with bookable resources while editing an order.
                return true;
            }

            return false;
        },

        minusButtonDisabled(ticketID) {
            return toInt(this.ticketQuantities[ticketID]) <= 0;
        },

        invalidTicketQuantity(ticketID) {
            return toInt(this.ticketQuantities[ticketID]) < 0;
        },

        async ok() {
            /**
             * @type {Ticket[]}
             */
            const selectedTickets = this.allTicketsForEvent.filter((ticket) => this.ticketQuantities[ticket.ID] > 0);

            /**
             * @type {{tickets: {ticket: Ticket, qty: Number}[], lastTicketSelectedCategory: Category, groupBookingReferenceData: string|null}}
             */
            const result = {
                tickets: selectedTickets.map((ticket) => ({
                    ticket,
                    qty: toInt(this.ticketQuantities[ticket.ID]),
                })),
                lastTicketSelectedCategory: this.lastTicketSelectedCategory,
            };

            // Persist the value of the assignGroupBookingReference toggle to the cart.
            await container.cartService.setAssignGroupBookingReference(
                this.assignGroupBookingReference,
                true
            );

            // If assignGroupBookingReference is enabled, we need to show the GroupBookingReferenceModal now.
            // Show this and wait for the user to enter the group booking reference data before resolving the
            // ticket picker modal.
            if (
                this.deviceCanManageGroupBookings
                && container.cartService.currentCart.assignGroupBookingReference
            ) {
                // If the assign group booking reference setting is enabled it means this must be a group booking.
                // Enable the group booking value on the cart too.
                await container.cartService.setGroupBooking(true, false);

                try {
                    const groupBookingReferenceData = await container.vueModalFactory
                        .open(
                            MODAL_NAMES__EPOS.GROUP_BOOKING_REFERENCE,
                            {
                                initialValue: this.groupBookingReference,
                            }
                        )
                        .catch(suppressModalCancelledErrorOrReThrow);

                    if (groupBookingReferenceData) {
                        result.groupBookingReferenceData = groupBookingReferenceData;
                    } else {
                        console.debug('No group booking reference data returned from modal.');
                        return;
                    }
                } catch (err) {
                    // If the user cancelled the modal, or if any error occurred, we can't continue
                    // because the assign reference toggle is set to true.
                    console.error('Error assigning group booking reference:', err);
                    return;
                }
            }

            this.resolve(result);
        },

        /**
         * Update the pricing-rule-adjusted prices for the visible tickets.
         */
        updateTicketPrices() {
            if (!this.session) {
                return;
            }

            if (!container.connectivityChecker.isOnline()) {
                return;
            }

            // Filter to tickets that do not have an adjustedPrice loaded yet,
            // then convert to an array of lines to send to the API to ask for the
            // adjusted price.
            /**
             * @type {{itemID: number, ticket: Ticket, sessionID: number}[]}
             */
            const ticketSessions = this.visibleTickets
                .filter((ticket) => this.adjustedPrices.hasOwnProperty(ticket.ID) === false)
                .map((ticket) => {
                    return {
                        itemID: ticket.ID,
                        sessionID: this.session.ID,
                    };
                });

            if (ticketSessions.length < 1) {
                // Nothing to do.
                return;
            }

            this.loading = true;
            container.pricingRuleCalculator.fetchSessionPricesForTickets(ticketSessions)
                .then((results) => {
                    Object.values(results).forEach((result) => {
                        this.adjustedPrices[result.itemID] = toNullableFloat(result.price);
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../../less/variables";

#ticket-picker-modal {
    .modl {
        height: 100%;
    }

    .modl-body {
        padding: 0;
        display: flex;
        flex-direction: column;
    }

    .ticket-picker-session {
        flex-grow: 0;
        flex-shrink: 0;
    }

    .item-panel {
        flex-grow: 1;
        flex-shrink: 1;
        margin: 0;
        padding: 0;
    }

    .item-panel__right {
        padding: 15px;
        border-radius: 0;
    }

    .ticket-picker__ticket {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        margin-bottom: 15px;
        padding-bottom: 15px;
        border-bottom: 1px solid #e5e5e5;

        &:last-child {
            margin-bottom: 0;
            border-bottom: 0;
        }
    }

    .ticket-picker__ticket-details {
        flex-grow: 1;
        flex-shrink: 1;
        font-size: 18px;
        min-width: 0;
    }

    i.ticket-picker__waiver-icon,
    i.ticket-picker__seats-icon {
        color: @dark-grey;
        font-size: @font-size-medium;
        margin-left: 5px;
        top: -2px;
    }

    .ticket-picker__ticket-price {
        font-size: .9em;
        opacity: .8;
    }

    .ticket-picker__ticket-btns {
        flex-grow: 0;
        flex-shrink: 0;
        display: flex;
        width: 180px;
        margin-left: 15px;
    }

    .ticket-picker__quantity-input {
        border: none;
        text-align: center;
        height: @round-btn-lg-size;
        line-height: @round-btn-lg-size;
        margin: 0 0 0 10px;
        width: 70px;
        font-size: 24px;
        &.invalid {
            color: @brand-danger;
        }
    }

    // The mobile category dropdown (CategoryNavDropdown). Hidden on desktop via .mobile-only.
    .ticket-picker__category-dropdown {
        padding: 10px;
    }
}

@media (max-width: @mobile-max) {
    #ticket-picker-modal {
        .item-panel {
            display: flex;
            flex-direction: column;
            height: 100%;
            min-height: 100%;
        }

        .item-panel__right {
            flex-grow: 1;
            height: auto;
        }
    }
}
</style>
